import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Category, TrendSignal } from './lib/trends';
import './styles.css';

export interface FeedPayload {
  source: 'public-x-search';
  generatedAt: string;
  partial: boolean;
  failedCategories: Category[];
  failedSearches: string[];
  scannedPostCount: number;
  postCount: number;
  signalCount: number;
  coverage: {
    searchCount: number;
    watchPosts: number;
    discoveryPosts: number;
    watchlistSignals: number;
    discoveredSignals: number;
  };
  signals: TrendSignal[];
}

interface AppProps {
  loadFeed?: () => Promise<FeedPayload>;
}

type Filter = 'all' | Category;
type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; feed: FeedPayload };

const CATEGORY_LABELS: Record<Category, string> = {
  camera: 'カメラ',
  youtube: 'YouTube',
  ai: 'AI',
};

const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'camera', label: 'カメラ' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'ai', label: 'AI' },
];

async function defaultLoadFeed(): Promise<FeedPayload> {
  const response = await fetch('/api/feed', { headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(`feed_status_${response.status}`);
  const payload: unknown = await response.json();
  if (!payload || typeof payload !== 'object' || !Array.isArray((payload as FeedPayload).signals)) {
    throw new Error('invalid_feed_payload');
  }
  return payload as FeedPayload;
}

function compactNumber(value: number): string {
  return new Intl.NumberFormat('ja-JP', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function relativeTime(timestamp: number): string {
  const seconds = Math.max(0, Math.round(Date.now() / 1000 - timestamp));
  if (seconds < 60) return 'たった今';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分前`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}時間前`;
  return `${Math.floor(seconds / 86_400)}日前`;
}

function SignalMark() {
  return (
    <svg aria-hidden="true" className="brand-mark" viewBox="0 0 48 48">
      <path d="M7 33c5-14 10-18 16-9 5 8 9 4 18-12" />
      <path d="M7 40c7-7 12-8 18-4 6 4 10 0 16-7" />
      <circle cx="39" cy="10" r="3" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20">
      <path d="M4 10h11M11 5l5 5-5 5" />
    </svg>
  );
}

function ScoreDial({ score }: { score: number }) {
  return (
    <div className="score-dial" style={{ '--score': `${score * 3.6}deg` } as React.CSSProperties}>
      <span className="score-value">{score}</span>
      <span className="score-unit">/ 100</span>
    </div>
  );
}

function SignalCard({ signal, rank }: { signal: TrendSignal; rank: number }) {
  const source = signal.samplePosts[0];
  return (
    <article className={`signal-card category-${signal.category}`}>
      <header className="signal-card__header">
        <div className="signal-card__meta">
          <span className="rank">{String(rank).padStart(2, '0')}</span>
          <span className="category-label">{CATEGORY_LABELS[signal.category]}</span>
          {signal.topicOrigin === 'discovered' || signal.topicOrigin === 'hashtag' ? (
            <span className="discovery-label">新語</span>
          ) : null}
        </div>
        <ScoreDial score={signal.score} />
      </header>

      <div className="signal-card__body">
        <h2>{signal.title}</h2>
        <p className="reason">{signal.reason}</p>

        <dl className="metrics">
          <div>
            <dt>POSTS</dt>
            <dd>{signal.postCount}</dd>
          </div>
          <div>
            <dt>VOICES</dt>
            <dd>{signal.uniqueAuthors}</dd>
          </div>
          <div>
            <dt>VIEWS</dt>
            <dd>{compactNumber(signal.totalViews)}</dd>
          </div>
        </dl>

        {source ? (
          <blockquote className="source-post">
            <p>{source.excerpt}</p>
            <footer>
              <span>@{source.author.screenName}</span>
              <span>{relativeTime(source.createdTimestamp)}</span>
            </footer>
          </blockquote>
        ) : null}
      </div>

      {source ? (
        <a
          className="source-link"
          href={source.url}
          rel="noopener noreferrer"
          target="_blank"
          aria-label={`元のポストを見る — @${source.author.screenName}`}
        >
          <span>元のポストを見る</span>
          <ArrowIcon />
        </a>
      ) : null}
    </article>
  );
}

function LoadingState() {
  return (
    <main className="state-screen" role="status" aria-live="polite">
      <SignalMark />
      <p className="eyebrow">X SIGNAL SCAN</p>
      <h1>観測中</h1>
      <div className="loading-line"><span /></div>
      <p>カメラ・YouTube・AI界隈の初速を読んでいます。</p>
    </main>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="state-screen error-state">
      <p className="eyebrow">SIGNAL LOST</p>
      <h1>いまは予報を更新できません</h1>
      <p>上流データとの接続が不安定です。公開済みでない情報は表示していません。</p>
      <button className="primary-button" type="button" onClick={onRetry}>
        もう一度観測する
      </button>
    </main>
  );
}

export function App({ loadFeed = defaultLoadFeed }: AppProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [filter, setFilter] = useState<Filter>('all');

  const refresh = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      setState({ status: 'ready', feed: await loadFeed() });
    } catch {
      setState({ status: 'error' });
    }
  }, [loadFeed]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibleSignals = useMemo(() => {
    if (state.status !== 'ready') return [];
    return filter === 'all'
      ? state.feed.signals
      : state.feed.signals.filter((signal) => signal.category === filter);
  }, [filter, state]);

  if (state.status === 'loading') return <LoadingState />;
  if (state.status === 'error') return <ErrorState onRetry={() => void refresh()} />;

  const strongest = state.feed.signals[0];
  const updateTime = new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(state.feed.generatedAt));

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="伸び予報 ホーム">
          <SignalMark />
          <span className="brand-jp">伸び予報</span>
          <span className="brand-en">NOBI<br />FORECAST</span>
        </a>
        <div className="observation-status">
          <span className="live-dot" />
          <span>最終観測 {updateTime}</span>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">EARLY SIGNALS / PUBLIC X DATA</p>
            <h1>明日伸びる話題を、<br />今日見つける。</h1>
            <p className="hero-lead">
              バズった後では遅い。投稿の初速、声の広がり、反応の密度から、
              カメラ・YouTube・AI界隈の「兆し」だけを観測します。
            </p>
          </div>

          <aside className="today-forecast" aria-label="現在の観測概要">
            <div className="forecast-topline">
              <span>TODAY'S PEAK</span>
              <span>{state.feed.partial ? '一部観測' : '観測良好'}</span>
            </div>
            <strong>{strongest?.score ?? '—'}</strong>
            <span className="forecast-caption">伸び予報スコア</span>
            <div className="forecast-topic">
              <span>最注目</span>
              <b>{strongest?.title ?? '観測データなし'}</b>
            </div>
          </aside>
        </section>

        <section className="signal-section" aria-labelledby="signal-heading">
          <div className="section-heading">
            <div>
              <p className="eyebrow">LIVE FORECAST</p>
              <h2 id="signal-heading">いま伸び始めた話題</h2>
            </div>
            <div className="scan-summary">
              <p>{state.feed.scannedPostCount}件を走査し、{state.feed.postCount}件から {state.feed.signalCount}件の兆しを抽出</p>
              <span>探索 {state.feed.coverage.discoveryPosts}件 / 既知 {state.feed.coverage.watchPosts}件</span>
            </div>
          </div>

          <nav className="filters" aria-label="カテゴリ">
            {FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </nav>

          {state.feed.partial ? (
            <p className="partial-notice" role="status">
              一部の観測経路が不安定なため、取得済みの予報だけを表示しています。
            </p>
          ) : null}

          {visibleSignals.length > 0 ? (
            <div className="signal-grid">
              {visibleSignals.map((signal, index) => (
                <SignalCard key={signal.key} signal={signal} rank={index + 1} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>このカテゴリでは、まだ十分な兆しを観測できていません。</p>
              <button type="button" onClick={() => setFilter('all')}>すべての予報を見る</button>
            </div>
          )}
        </section>

        <section className="methodology" aria-labelledby="method-heading">
          <div>
            <p className="eyebrow">HOW IT READS THE AIR</p>
            <h2 id="method-heading">人気ではなく、<br />変化を読む。</h2>
          </div>
          <ol>
            <li><span>01</span><div><b>二系統で探す</b><p>既知語の監視と、6時間ごとに切り替わる広域探索を並行します。</p></div></li>
            <li><span>02</span><div><b>新語を束ねる</b><p>固定辞書にない製品名、サービス名、ハッシュタグも複数投稿者から抽出します。</p></div></li>
            <li><span>03</span><div><b>初速を測る</b><p>鮮度、反応、ユニーク投稿者数を合わせて相対評価します。</p></div></li>
          </ol>
        </section>
      </main>

      <footer className="site-footer">
        <div className="brand footer-brand"><SignalMark /><span className="brand-jp">伸び予報</span></div>
        <p>公開情報の観測結果であり、将来の伸長を保証するものではありません。画像・動画は再配布しません。</p>
        <span>© 2026 NOBI FORECAST</span>
      </footer>
    </div>
  );
}
